/**
 * LabelMultiSelect — Popover multi-select with inline "create new label"
 * flow. Used by TaskDialog (and later by any inline task composer that
 * wants labels).
 *
 * Props:
 *   value: string[]                  — currently selected label ids
 *   onChange: (nextIds: string[]) => void
 *   compact?: boolean                — smaller trigger for dense rows
 *   testIdPrefix?: string            — for data-testid namespacing
 */
import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Plus, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { useLabelsStore } from "../../store/labelsStore";
import { LIST_PALETTE, getListPalette } from "../../constants/listPalette";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { formatApiErrorDetail } from "../../lib/api";

export default function LabelMultiSelect({
  value = [],
  onChange,
  compact = false,
  testIdPrefix = "label-select",
}) {
  const { labels, loaded, fetchLabels, createLabel } = useLabelsStore();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [newColor, setNewColor] = useState("indigo");

  useEffect(() => {
    if (!loaded) fetchLabels().catch(() => {});
  }, [loaded, fetchLabels]);

  const selected = useMemo(
    () => labels.filter((l) => value.includes(l.id)),
    [labels, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return labels;
    return labels.filter((l) => l.name.toLowerCase().includes(q));
  }, [labels, query]);

  const exactMatch = query.trim()
    ? labels.find((l) => l.name.toLowerCase() === query.trim().toLowerCase())
    : null;

  const toggle = (id) => {
    const next = value.includes(id)
      ? value.filter((x) => x !== id)
      : [...value, id];
    onChange(next);
  };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name) return;
    setCreating(true);
    try {
      const l = await createLabel({ name, color: newColor });
      if (!value.includes(l.id)) onChange([...value, l.id]);
      setQuery("");
      toast.success(`"${l.name}" etiketi oluşturuldu`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Etiket oluşturulamadı",
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testIdPrefix}-trigger`}
          className={[
            "inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white text-sm text-zinc-700 hover:bg-zinc-50 transition-colors",
            compact ? "h-8 px-2 text-xs" : "h-9 px-3",
            "w-full justify-between",
          ].join(" ")}
        >
          <span className="flex flex-1 items-center gap-1.5 flex-wrap min-w-0">
            <Tag className="h-3.5 w-3.5 text-zinc-400 shrink-0" strokeWidth={2} />
            {selected.length === 0 ? (
              <span className="text-zinc-500">Etiket ekle…</span>
            ) : (
              selected.map((l) => {
                const p = getListPalette(l.color);
                return (
                  <span
                    key={l.id}
                    className={[
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                      l.color ? p.chip : "bg-zinc-100 text-zinc-700",
                    ].join(" ")}
                    data-testid={`${testIdPrefix}-selected-${l.id}`}
                  >
                    {l.emoji ? <span aria-hidden>{l.emoji}</span> : null}
                    <span>{l.name}</span>
                  </span>
                );
              })
            )}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-2"
        data-testid={`${testIdPrefix}-popover`}
        align="start"
      >
        <div className="space-y-2">
          <Input
            placeholder="Etiket ara veya oluştur…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && query.trim() && !exactMatch) {
                e.preventDefault();
                handleCreate();
              }
            }}
            data-testid={`${testIdPrefix}-search`}
            className="h-8 text-sm"
            autoFocus
          />

          {/* Palette picker — only shown when the user is about to create a
              brand-new label so they can immediately colorize it. */}
          {query.trim() && !exactMatch && (
            <div className="rounded-md border border-dashed border-zinc-200 bg-zinc-50 p-2">
              <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold text-zinc-500">
                <span>Yeni etiket rengi</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {LIST_PALETTE.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setNewColor(p.key)}
                    title={p.label}
                    data-testid={`${testIdPrefix}-new-color-${p.key}`}
                    className={[
                      "h-5 w-5 rounded-full ring-1 ring-black/10 transition-transform",
                      newColor === p.key ? "scale-125 ring-2 ring-zinc-900" : "hover:scale-110",
                    ].join(" ")}
                    style={{ backgroundColor: p.hex }}
                  />
                ))}
              </div>
              <Button
                type="button"
                size="sm"
                onClick={handleCreate}
                disabled={creating}
                data-testid={`${testIdPrefix}-create`}
                className="mt-2 h-7 w-full bg-zinc-900 text-white hover:bg-zinc-800"
              >
                <Plus className="mr-1 h-3 w-3" strokeWidth={2.5} />
                {creating ? "Ekleniyor…" : `"${query.trim()}" etiketini oluştur`}
              </Button>
            </div>
          )}

          {/* Existing labels list */}
          <div
            className="max-h-64 overflow-y-auto space-y-0.5"
            data-testid={`${testIdPrefix}-options`}
          >
            {filtered.length === 0 && !query.trim() && (
              <div className="py-4 text-center text-xs text-zinc-400">
                Henüz etiket yok. Yukarıdan yeni bir tane oluştur.
              </div>
            )}
            {filtered.map((l) => {
              const isOn = value.includes(l.id);
              const p = getListPalette(l.color);
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => toggle(l.id)}
                  data-testid={`${testIdPrefix}-option-${l.id}`}
                  className={[
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                    isOn ? "bg-zinc-100" : "hover:bg-zinc-50",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                      l.color ? p.chip : "bg-zinc-100 text-zinc-700",
                    ].join(" ")}
                  >
                    {l.emoji ? <span aria-hidden>{l.emoji}</span> : null}
                    <span>{l.name}</span>
                  </span>
                  <span className="flex-1" />
                  {isOn ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.5} />
                  ) : null}
                </button>
              );
            })}
          </div>

          {selected.length > 0 && (
            <div className="border-t border-zinc-100 pt-1.5">
              <button
                type="button"
                onClick={() => onChange([])}
                data-testid={`${testIdPrefix}-clear`}
                className="flex w-full items-center justify-center gap-1 rounded-md py-1 text-[11px] text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700"
              >
                <X className="h-3 w-3" /> Tüm etiketleri kaldır
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
